// src/data/aiToolsList.js

// Tool categories - useful for filtering and organizing tools
export const TOOL_CATEGORIES = {
  LIFESTYLE: 'lifestyle',
  UTILITY: 'utility', 
  FINANCE: 'finance',
  IMMIGRATION: 'immigration',
  EDUCATION: 'education',
  CULTURAL: 'cultural',
  COMMUNICATION: 'communication',
  HEALTH: 'health' 
};

// Static fallback data - preserved exactly as before
const STATIC_AI_TOOLS_DATA = [
  { 
    id: 'age-calculator', 
    title: 'Advanced Age Calculator', 
    description: 'Advanced Age & Milestone Calculator: Track your precise age live, count down to your next birthday, and discover dates for key life milestones (birthdays and total days).', 
    icon: '🎂', 
    path: '/ai-tools/age-calculator', 
    requiresCredits: false, 
    comingSoon: false,
    creditCost: 0, // Free tool
    isNew: false,
    isFree: true,
    category: TOOL_CATEGORIES.UTILITY
  },

  {
    id: 'exchange-rate-tool',
    title: 'Mid-Market Exchange Rate Tool',
    description: 'Check indicative mid-market rates for sending money overseas and understand potential costs.',
    icon: '💹',
    path: '/ai-tools/exchange-rate-tool',
    requiresCredits: false,
    comingSoon: false,
    creditCost: 0, // Free tool
    isNew: true,
    isFree: true,
    category: TOOL_CATEGORIES.FINANCE
  },
  
  // Health & Fitness Tools (Free)
  {
    id: 'bmi-calculator',
    title: 'BMI Calculator',
    description: 'Calculate your Body Mass Index (BMI) with metric or imperial units and understand your health category',
    icon: '⚖️',
    path: '/ai-tools/bmi-calculator',
    requiresCredits: false,
    comingSoon: false,
    creditCost: 0, // Free tool
    isNew: true,
    isFree: true,
    category: TOOL_CATEGORIES.HEALTH
  },
  {
    id: 'calorie-calculator',
    title: 'Calorie Calculator',
    description: 'Calculate your daily calorie needs based on age, gender, activity level, and fitness goals',
    icon: '🍎',
    path: '/ai-tools/calorie-calculator',
    requiresCredits: false,
    comingSoon: false,
    creditCost: 0, // Free tool
    isNew: true,
    isFree: true,
    category: TOOL_CATEGORIES.HEALTH
  },
  {
    id: 'body-fat-percentage',
    title: 'Body Fat Percentage Calculator',
    description: 'Estimate your body fat percentage using various measurement methods and formulas',
    icon: '📊',
    path: '/ai-tools/body-fat-percentage',
    requiresCredits: false,
    comingSoon: false,
    creditCost: 0, // Free tool
    isNew: true,
    isFree: true,
    category: TOOL_CATEGORIES.HEALTH
  },
  {
    id: 'water-intake-calculator',
    title: 'Water Intake Calculator',
    description: 'Calculate your daily water intake needs based on your weight and activity level',
    icon: '💧',
    path: '/ai-tools/water-intake-calculator',
    requiresCredits: false,
    comingSoon: false,
    creditCost: 0, // Free tool
    isNew: true,
    isFree: true,
    category: TOOL_CATEGORIES.HEALTH
  },

  {
    id: 'pregnancy-due-date',
    title: 'Pregnancy Due Date Calculator',
    description: 'Calculate your pregnancy due date using your last menstrual period or conception date',
    icon: '🤰',
    path: '/ai-tools/pregnancy-due-date',
    requiresCredits: false,
    comingSoon: false,
    creditCost: 0, // Free tool
    isNew: true,
    isFree: true,
    category: TOOL_CATEGORIES.HEALTH
  },
  {
    id: 'baby-growth-tracker',
    title: 'Baby Growth Tracker',
    description: 'Track your baby\'s growth, development milestones, and immunisation schedule with Australian health standards',
    icon: '👶',
    path: '/ai-tools/baby-growth-tracker',
    requiresCredits: false,
    comingSoon: false,
    creditCost: 0, // Free tool
    isNew: true,
    isFree: true,
    category: TOOL_CATEGORIES.HEALTH
  },

  { 
    id: 'australian-trip-planner', 
    title: 'Australian Hidden Gems Trip Planner', 
    description: 'Discover incredible Australian destinations from our comprehensive database of verified tourist attractions nationwide. Perfect for anyone exploring Australia with practical travel tips and cultural insights.', 
    icon: '🗺️', 
    path: '/ai-tools/australian-trip-planner', 
    requiresCredits: false, 
    comingSoon: false,
    creditCost: 0,
    isNew: true,
    isFree: true,
    category: TOOL_CATEGORIES.UTILITY
  },

  {
    id: 'jet-lag-recovery-planner',
    title: 'Jet Lag Recovery Planner',
    description: 'Calculate adjustment schedules when traveling between Australia and South Asia to minimize jet lag effects.',
    icon: '✈️',
    path: '/ai-tools/jet-lag-recovery-planner',
    requiresCredits: true, 
    comingSoon: false,
    isNew: true,
    isFree: false, 
    category: TOOL_CATEGORIES.UTILITY
  },
 
  { 
    id: 'business-ideas-generator',
    title: 'AI Business Ideas Generator',
    description: 'Generate innovative business ideas tailored for South Asian entrepreneurs with market analysis and growth plans',
    icon: '💡',
    path: '/ai-tools/business-ideas-generator',
    requiresCredits: true,
    comingSoon: false,
    isNew: true,
    isFree: false,
    premium: true,
    category: TOOL_CATEGORIES.UTILITY
  },
  { 
    id: 'business-generator', 
    title: 'Desi Business Name Generator', 
    description: 'Create culturally relevant business names', 
    icon: '🏢', 
    path: '/ai-tools/business-generator', 
    requiresCredits: true, 
    comingSoon: false,
    isNew: false,
    isFree: false,
    category: TOOL_CATEGORIES.UTILITY
  },
  { 
    id: 'product-description-generator', 
    title: 'Desi Product Description Generator', 
    description: 'Create compelling product descriptions in English and South Asian languages for your business', 
    icon: '📝', 
    path: '/ai-tools/product-description-generator', 
    requiresCredits: true, 
    comingSoon: false,
    isNew: true,
    isFree: false,
    category: TOOL_CATEGORIES.UTILITY
  },
  { 
    id: 'invoice-builder', 
    title: 'Smart Invoice Builder', 
    description: 'Create professional invoices with AI-enhanced descriptions, payment terms and summaries. Download as PDF for immediate use.', 
    icon: '📊', 
    path: '/ai-tools/invoice-builder', 
    requiresCredits: true, 
    comingSoon: false,
    isNew: true,
    isFree: false,
    category: TOOL_CATEGORIES.FINANCE
  },
  { 
    id: 'property-development-planner', 
    title: 'Property Development Planner', 
    description: 'Calculate what you can build on your property based on Australian council regulations, zoning laws, and property dimensions', 
    icon: '🏠', 
    path: '/ai-tools/property-development-planner', 
    requiresCredits: true, 
    comingSoon: false,
    isNew: true,
    isFree: false,
    premium: true,
    category: TOOL_CATEGORIES.UTILITY
  },
  {
    id: 'investment-property-analyzer',
    title: 'Investment Property Calculator',
    description: 'Free Australian investment property calculator — analyse cash flow, rental yield, negative gearing tax benefits, stamp duty, LMI, and capital growth projections. No signup required.',
    icon: '🏘️',
    path: '/ai-tools/investment-property-analyzer',
    requiresCredits: false,
    comingSoon: false,
    creditCost: 0,
    isNew: true,
    isFree: true,
    category: 'property'
  },
  { 
    id: 'visa_guidance',
    title: 'Australian Visa Pathway Explorer',
    description: 'Analyze potential visa options based on your profile and get informed guidance on possible pathways',
    icon: '🛂',
    path: '/ai-tools/visa-guidance',
    requiresCredits: true,
    comingSoon: false,
    isNew: false,
    isFree: false,
    premium: true,
    category: TOOL_CATEGORIES.IMMIGRATION
  },
  { 
    id: 'diet-planner',
    title: 'Desi Diet & Weight Management',
    description: 'Get culturally-appropriate diet plans with Australian ingredient substitutions',
    icon: '🥗',
    path: '/ai-tools/diet-planner',
    requiresCredits: true,
    comingSoon: false,
    isNew: false,
    isFree: false,
    premium: true,
    category: TOOL_CATEGORIES.LIFESTYLE
  },
  { 
    id: 'desi_food_planner',
    title: 'Desi Party Food Planner',
    description: 'Plan perfect South Asian food quantities for parties, weddings, and cultural events. Get customized menus, shopping lists, and serving recommendations.',
    icon: '🍲',
    path: '/ai-tools/desi-food-planner',
    requiresCredits: true,
    comingSoon: false,
    isNew: true,
    isFree: false,
    premium: true,
    category: TOOL_CATEGORIES.CULTURAL
  },
  { 
    id: 'gift-ideas-generator', 
    title: 'Gift Ideas Generator', 
    description: 'Discover perfect gift ideas for any occasion and culture. Get personalized suggestions that celebrate diversity and create meaningful connections across all backgrounds and traditions.', 
    icon: '🎁', 
    path: '/ai-tools/gift-ideas-generator', 
    requiresCredits: true, 
    comingSoon: false,
    isNew: true,
    isFree: false,
    category: TOOL_CATEGORIES.CULTURAL
  },
  {
    id: 'housing-affordability-advisor',
    title: 'Home Loan Affordability Calculator',
    description: 'Free Australian home loan calculator — find your maximum borrowing power, compare loan scenarios, stress-test rate rises, and discover first home buyer grants and stamp duty concessions.',
    icon: '🏡',
    path: '/ai-tools/housing-affordability-advisor',
    requiresCredits: false,
    comingSoon: false,
    creditCost: 0,
    isNew: true,
    isFree: true,
    category: 'property'
  },
  { 
    id: 'accent-coach', 
    title: 'Accent & Communication Coach', 
    description: 'Improve workplace communication with AI-powered pronunciation feedback', 
    icon: '🗣️', 
    path: '/ai-tools/accent-coach', 
    requiresCredits: true, 
    comingSoon: true,
    creditCost: 0, // Will cost credits when released
    isNew: false,
    isFree: false,
    category: TOOL_CATEGORIES.COMMUNICATION
  },

  // NAPLAN Preparation Tools
  {
    id: 'naplan-math-practice',
    title: 'NAPLAN Numeracy Practice',
    description: 'Free adaptive numeracy practice for Years 3, 5, 7 and 9 with 3 difficulty tiers, timed mode, calculator sections, and NAPLAN performance bands.',
    icon: '🧮',
    path: '/ai-tools/naplan-math-practice',
    requiresCredits: false,
    comingSoon: false,
    creditCost: 0,
    isNew: true,
    isFree: true,
    category: TOOL_CATEGORIES.EDUCATION
  },
  {
    id: 'naplan-reading-practice',
    title: 'NAPLAN Reading Practice',
    description: 'Free reading comprehension practice with 40+ passages across all year levels. Tests literal, inference, vocabulary, structure, and purpose skills.',
    icon: '📖',
    path: '/ai-tools/naplan-reading-practice',
    requiresCredits: false,
    comingSoon: false,
    creditCost: 0,
    isNew: true,
    isFree: true,
    category: TOOL_CATEGORIES.EDUCATION
  },
  {
    id: 'naplan-conventions-practice',
    title: 'NAPLAN Conventions of Language',
    description: 'Free spelling, grammar, and punctuation practice for Years 3-9. Covers homophones, apostrophes, sentence structure, and more.',
    icon: '✏️',
    path: '/ai-tools/naplan-conventions-practice',
    requiresCredits: false,
    comingSoon: false,
    creditCost: 0,
    isNew: true,
    isFree: true,
    category: TOOL_CATEGORIES.EDUCATION
  },
  {
    id: 'naplan-writing-practice',
    title: 'NAPLAN Writing Practice',
    description: 'Practice narrative and persuasive writing with NAPLAN-style prompts. Free timed practice with optional AI feedback using the official 10-criteria rubric.',
    icon: '📝',
    path: '/ai-tools/naplan-writing-practice',
    requiresCredits: false,
    comingSoon: false,
    creditCost: 0,
    isNew: true,
    isFree: true,
    category: TOOL_CATEGORIES.EDUCATION
  },
  {
    id: 'times-tables-challenge',
    title: 'Times Tables Speed Challenge',
    description: 'Master your multiplication tables with fun practice modes. Practice mode, speed challenges, and focused table training with progress tracking.',
    icon: '✖️',
    path: '/ai-tools/times-tables-challenge',
    requiresCredits: false,
    comingSoon: false,
    creditCost: 0,
    isNew: true,
    isFree: true,
    category: TOOL_CATEGORIES.EDUCATION
  },
  {
    id: 'homework-helper',
    title: 'Homework Helper',
    description: 'Type or photograph any homework question and get step-by-step solutions. Free hints for everyone, full solutions with credits.',
    icon: '\uD83C\uDFE0',
    path: '/ai-tools/homework-helper',
    requiresCredits: false,
    comingSoon: false,
    isNew: true,
    isFree: true,
    category: TOOL_CATEGORIES.EDUCATION
  },
  {
    id: 'essay-reviewer',
    title: 'Essay Reviewer',
    description: 'Submit your essay and get rubric-based feedback scored on Ideas, Structure, Vocabulary, Grammar and Style.',
    icon: '\uD83D\uDCDA',
    path: '/ai-tools/essay-reviewer',
    requiresCredits: false,
    comingSoon: false,
    isNew: true,
    isFree: true,
    category: TOOL_CATEGORIES.EDUCATION
  },
  {
    id: 'math-problem-solver',
    title: 'Math Problem Solver',
    description: 'Type any math problem and get a clear step-by-step solution with explanations. From basic arithmetic to advanced algebra and geometry.',
    icon: '🔢',
    path: '/ai-tools/math-problem-solver',
    requiresCredits: true,
    comingSoon: false,
    isNew: true,
    isFree: false,
    category: TOOL_CATEGORIES.EDUCATION
  },
  {
    id: 'maths-tutor',
    title: 'Australian Maths Tutor',
    description: 'Comprehensive maths help for Years 2–12. BIDMAS, fractions, algebra, Pythagoras, calculus and more. ACARA v9 aligned. Practice questions, AI solver and free printable worksheets.',
    icon: '🧮',
    path: '/ai-tools/maths-tutor',
    requiresCredits: false,
    comingSoon: false,
    isNew: true,
    isFree: true,
    category: TOOL_CATEGORIES.EDUCATION
  },
  {
    id: 'education-navigator',
    title: 'Education Navigator',
    description: 'Find suitable schools and understand the Australian education system',
    icon: '📚',
    path: '/ai-tools/education-navigator',
    requiresCredits: true,
    comingSoon: true,
    creditCost: 0, // Will cost credits when released
    isNew: false,
    isFree: false,
    category: TOOL_CATEGORIES.EDUCATION
  },
  { 
    id: 'wedding-planner', 
    title: 'Wedding Planning Assistant', 
    description: 'Plan traditional weddings in Australian settings', 
    icon: '💍', 
    path: '/ai-tools/wedding-planner', 
    requiresCredits: true, 
    comingSoon: true,
    creditCost: 0, // Will cost credits when released
    isNew: false,
    isFree: false,
    category: TOOL_CATEGORIES.LIFESTYLE
  },
  { 
    id: 'etiquette-translator', 
    title: 'Cultural Etiquette Translator', 
    description: 'Learn Australian social norms in South Asian context', 
    icon: '🤝', 
    path: '/ai-tools/etiquette-translator', 
    requiresCredits: true, 
    comingSoon: true,
    isNew: false,
    isFree: false,
    category: TOOL_CATEGORIES.CULTURAL
  },
  { 
    id: 'skill-recognition', 
    title: 'Skill Recognition Guide', 
    description: 'Understand how your qualifications translate to Australian requirements', 
    icon: '📝', 
    path: '/ai-tools/skill-recognition', 
    requiresCredits: true, 
    comingSoon: true,
    isNew: false,
    isFree: false,
    category: TOOL_CATEGORIES.EDUCATION
  },
  { 
    id: 'voice-translator', 
    title: 'Voice-to-Text Translator', 
    description: 'Transcribe voice notes in regional languages to English', 
    icon: '🎤', 
    path: '/ai-tools/voice-translator', 
    requiresCredits: true, 
    comingSoon: true,
    isNew: false,
    isFree: false,
    category: TOOL_CATEGORIES.COMMUNICATION
  }
];

// Helper function to get tool by ID (supports both static and dynamic data)
export const getToolById = async (id) => {
  try {
    const data = await getAIToolsData();
    return data.find(tool => tool.id === id) || null;
  } catch (error) {
    return aiToolsData.find(tool => tool.id === id) || null;
  }
};

// Synchronous version for compatibility
export const getToolByIdSync = (id) => {
  return aiToolsData.find(tool => tool.id === id) || null;
};

// Helper function to get tool credit cost
export const getToolCreditCost = async (id) => {
  const tool = await getToolById(id);
  return tool ? tool.creditCost : 0;
};

// Synchronous version for compatibility
export const getToolCreditCostSync = (id) => {
  const tool = getToolByIdSync(id);
  return tool ? tool.creditCost : 0;
};

// Get all active (not coming soon) tools
export const getActiveTools = async () => {
  try {
    const data = await getAIToolsData();
    return data.filter(tool => !tool.comingSoon);
  } catch (error) {
    return aiToolsData.filter(tool => !tool.comingSoon);
  }
};

// Get all free tools
export const getFreeTools = async () => {
  try {
    const data = await getAIToolsData();
    return data.filter(tool => tool.isFree && !tool.comingSoon);
  } catch (error) {
    return aiToolsData.filter(tool => tool.isFree && !tool.comingSoon);
  }
};

// Get all paid tools
export const getPaidTools = async () => {
  try {
    const data = await getAIToolsData();
    return data.filter(tool => !tool.isFree && !tool.comingSoon);
  } catch (error) {
    return aiToolsData.filter(tool => !tool.isFree && !tool.comingSoon);
  }
};

// Get tools by category
export const getToolsByCategory = async (category) => {
  try {
    const data = await getAIToolsData();
    return data.filter(tool => tool.category === category && !tool.comingSoon);
  } catch (error) {
    return aiToolsData.filter(tool => tool.category === category && !tool.comingSoon);
  }
};

// Get all available categories with at least one active tool
export const getActiveCategories = async () => {
  try {
    const activeTools = await getActiveTools();
    const categorySet = new Set(activeTools.map(tool => tool.category));
    return Array.from(categorySet);
  } catch (error) {
    const activeTools = aiToolsData.filter(tool => !tool.comingSoon);
    const categorySet = new Set(activeTools.map(tool => tool.category));
    return Array.from(categorySet);
  }
};

// Dynamic data loading functionality
let cachedToolsData = null;
let cachedToolsMeta = null;
let isLoading = false;

// Fetch AI tools from Django API
export const fetchAIToolsFromAPI = async () => {
  if (cachedToolsData) {
    return cachedToolsData;
  }

  if (isLoading) {
    // Wait for existing request to complete
    while (isLoading) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    return cachedToolsData;
  }

  isLoading = true;

  try {
    const response = await fetch('/api/aitools/dynamic-tools-list/', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': document.querySelector('[name=csrfmiddlewaretoken]')?.value || ''
      },
      credentials: 'same-origin'
    });

    if (response.ok) {
      const data = await response.json();
      if (data.success && data.aiToolsData) {
        cachedToolsData = data.aiToolsData;
        cachedToolsMeta = data.meta || null;
        console.log('✅ AI Tools loaded from API:', cachedToolsData.length, 'tools');
        return cachedToolsData;
      }
    }

    console.warn('⚠️ API failed, falling back to static data');
    throw new Error('API response invalid');

  } catch (error) {
    console.warn('⚠️ Error fetching from API, using static fallback:', error.message);
    cachedToolsData = STATIC_AI_TOOLS_DATA;
    cachedToolsMeta = null;
    return cachedToolsData;
  } finally {
    isLoading = false;
  }
};

// Get cached meta data (available after fetchAIToolsFromAPI resolves)
export const getAIToolsMeta = () => cachedToolsMeta;

// Export static data as fallback (safe approach)
export const aiToolsData = STATIC_AI_TOOLS_DATA;

// New async function to get dynamic data
export const getAIToolsData = async () => {
  try {
    return await fetchAIToolsFromAPI();
  } catch (error) {
    console.warn('⚠️ Using static fallback data');
    return STATIC_AI_TOOLS_DATA;
  }
};